<script lang="ts" setup>
import { ref, computed, inject, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import axiosInstance from '@/plugins/axios';
import { useToast } from 'primevue/usetoast';
import { ITranslation, Translation } from '@airin/shared-types';
import { computedAsync } from '@vueuse/core';

const gF = inject('gF') as any;
const route = useRoute();
const toast = useToast();

const isSaving = ref(false);
const translationID = computed(() => route.params.translation as string);
const questionID = computed(() => route.params.questionID as string);
const targetLanguage = computed(() => route.params.translation || 'error');

const fallback = new Translation();
fallback.questions = {};

const rootTranslation = computedAsync(async () => {
  const id =
    targetLanguage.value === '670e1e32d4634dd3c3ec7478'
      ? '670fad078be48456eb53b250'
      : targetLanguage.value;
  try {
    const response = await axiosInstance.get(`/translations/${id}`);
    const result = response.data ?? new Translation();
    if (!result.questions) result.questions = {};
    return result;
  } catch {
    return fallback;
  }
}, {
  initialValue: fallback,
});

const translation = computedAsync(async () => {
  try {
    const response = await axiosInstance.get(`/translations/${targetLanguage.value}`);
    const result = response.data ?? new Translation();
    if (!result.questions) result.questions = {};
    return result;
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Translation could not be loaded: ' + error,
      life: 3000,
    });
    return fallback;
  }
}, {
  initialValue: fallback,
});

onMounted(async () => {

});

const saveTranslation = async () => {
  gF.startLoading();
  isSaving.value = true;
  try {
    const payload = translation.value.questions[questionID.value];
    await axiosInstance.put(
      `/translations/updateQuestion/${translationID.value}/${questionID.value}`,
      payload,
    );
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Translation saved successfully',
      life: 3000,
    });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Translation could not be saved: ' + error,
      life: 3000,
    });
  } finally {
    gF.endLoading();
    isSaving.value = false;
  }
};
</script>

<template>
  <div v-if="rootTranslation.questions?.[questionID] && translation.questions?.[questionID]">
    <div class="row">
      <div class="row col-12 justify-content-center">
        <div class="col text-center mb-3">
          <Button
            class="w-30 p-button-tuev"
            label="Save"
            @click="saveTranslation"
            iconPos="left"
            :loading="isSaving"
          >
            <template #icon>
              <font-awesome-icon :icon="['fas', 'save']" />
            </template>
          </Button>
        </div>
      </div>

      <Card class="col-12 m-2">
        <template #title><div class="font-color-tuev">Title</div></template>
        <template #content>
          <div class="row gap-2">
            <div class="col-3"><label>Default (en)</label></div>
            <div class="col-8">
              {{ rootTranslation.questions[questionID].title }}
            </div>
            <div class="col-3"><label>{{ translation.languageCode }}</label></div>
            <div class="col-8">
              <InputText
                v-model="translation.questions[questionID].title"
                class="w-100"
              />
            </div>
          </div>
        </template>
      </Card>

      <Card class="col-12 m-2">
        <template #title><div class="font-color-tuev">Question Text</div></template>
        <template #content>
          <div class="row gap-2">
            <div class="col-3"><label>Default (en)</label></div>
            <div class="col-8">
              {{ rootTranslation.questions[questionID].questionText || 'No predefined translation available.' }}
            </div>
            <div class="col-3"><label>{{ translation.languageCode }}</label></div>
            <div class="col-8">
              <InputText
                v-model="translation.questions[questionID].questionText"
                class="w-100"
              />
            </div>
          </div>
        </template>
      </Card>

      <Card class="col-12 m-2" v-if="rootTranslation.questions[questionID].lawText || translation.questions[questionID].lawText">
        <template #title><div class="font-color-tuev">Law Text</div></template>
        <template #content>
          <div class="row gap-2">
            <div class="col-3"><label>Default (en)</label></div>
            <div
              class="col-8"
              v-html="rootTranslation.questions[questionID].lawText || 'No predefined translation available.'"
            ></div>
            <div class="col-3"><label>{{ translation.languageCode }}</label></div>
            <div class="col-8">
              <LimitedEditor
                v-model="translation.questions[questionID].lawText"
                class="w-100"
              />
            </div>
          </div>
        </template>
      </Card>

      <Card class="col-12 m-2" v-if="rootTranslation.questions[questionID].questionHelp || translation.questions[questionID].questionHelp">
        <template #title><div class="font-color-tuev">Question Help</div></template>
        <template #content>
          <div class="row gap-2">
            <div class="col-3"><label>Default (en)</label></div>
            <div
              class="col-8"
              v-html="rootTranslation.questions[questionID].questionHelp || 'No predefined translation available.'"
            ></div>
            <div class="col-3"><label>{{ translation.languageCode }}</label></div>
            <div class="col-8">
              <LimitedEditor
                v-model="translation.questions[questionID].questionHelp"
                class="w-100"
              />
            </div>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>
